import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Card, CardBody } from '@/shared/components/ui/Card';
import { Search, FileText, ShoppingCart, CreditCard, Users, BookOpen } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';

const TYPE_ICONS: Record<string, any> = { invoice: FileText, bill: ShoppingCart, payment: CreditCard, customer: Users, vendor: Users, journal: BookOpen };

// Only 'invoice' opens onto a real detail view today (InvoiceList.tsx,
// wired 2026-08-23). Bill/Payment have list screens but no single-record
// drill-in; Customer/Vendor (legacy AccountsCustomerModel/AccountsVendorModel)
// have no frontend view at all — Accounting V2's party pages run on a
// separate model with no reliable id correspondence. Rather than link to a
// page that can't resolve the id, those stay plain, informational rows.
function linkFor(result: { type: string; id: string }): string | null {
  if (result.type === 'invoice') return `${ROUTES.accountsSales}?invoiceId=${encodeURIComponent(result.id)}`;
  return null;
}

export function SearchPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['accounts', 'search', query, type],
    queryFn: async () => { if (query.length < 2) return null; const { data } = await api.get(`/api/accounts/search?q=${encodeURIComponent(query)}&type=${type}`); return data as { query: string; resultCount: number; results: any[] }; },
    enabled: query.length >= 2,
  });

  return (
    <div className="space-y-4 pb-16">
      <PageHeader icon={Search} eyebrow="Finance" title="Accounting Search" description={<>Search invoices, bills, vouchers, payments, parties by number, name, amount or reference</>} />

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input name="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Type to search (min 2 chars)..." className="pl-10" />
        </div>
        <Select name="type" value={type} onChange={e => setType(e.target.value)} options={[
          { value: 'all', label: 'All Types' }, { value: 'invoice', label: 'Invoices' }, { value: 'bill', label: 'Bills' },
          { value: 'payment', label: 'Payments' }, { value: 'journal', label: 'Journals' }, { value: 'customer', label: 'Customers' }, { value: 'vendor', label: 'Vendors' },
        ]} />
      </div>

      {isLoading && query.length >= 2 && <div className="text-sm text-muted-foreground py-4">Searching...</div>}

      {data && (
        <Card className="shadow-xs">
          <CardBody className="p-0">
            <div className="px-4 py-2 bg-muted/30 text-xs text-muted-foreground">{data.resultCount} result(s) for "{data.query}"</div>
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {data.results.map((r: any, i: number) => {
                const Icon = TYPE_ICONS[r.type] || FileText;
                const href = linkFor(r);
                return (
                  <div
                    key={i}
                    onClick={href ? () => navigate(href) : undefined}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-muted/20 ${href ? 'cursor-pointer' : ''}`}
                    title={href ? 'Open this invoice' : undefined}
                  >
                    <div className="p-1.5 rounded bg-muted"><Icon className="h-4 w-4 text-muted-foreground" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><span className="font-medium text-sm">{r.ref}</span><span className="text-xs px-1.5 py-0.5 rounded bg-muted capitalize">{r.type}</span>{r.status && <span className="text-xs px-1.5 py-0.5 rounded bg-muted">{r.status}</span>}</div>
                      <p className="text-xs text-muted-foreground truncate">{r.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      {r.amount > 0 && <p className="text-sm font-medium">{formatINR(r.amount)}</p>}
                      {r.date && <p className="text-xs text-muted-foreground">{new Date(r.date).toLocaleDateString('en-IN')}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>
      )}

      {query.length >= 2 && data?.resultCount === 0 && <div className="text-center py-8 text-muted-foreground">No results found.</div>}
    </div>
  );
}
